import { useState, useMemo, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Pencil, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { type CampaignData } from '@/lib/types';

interface SourceCampaignPickerProps {
  campaigns: CampaignData[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  /** Soft cap on rendered rows. Anything beyond shows a "+N more — refine search" hint. Default 50. */
  visibleCap?: number;
  /** Empty-state copy shown when the eligible list is empty. */
  emptyState?: React.ReactNode;
  /** Optional per-row gate. Return a node to render inline below the row's metadata and
   * disable selection on that row. Used by HiPo to block campaigns with no target_locations
   * while exposing a CTA to add one. */
  getDisabledReason?: (campaign: CampaignData) => React.ReactNode | null;
  /** When true, render a loading state instead of the empty state — prevents the picker from
   * looking broken while the campaigns query is still in flight. */
  isLoading?: boolean;
  /** When true, collapse the picker to a single-row summary once a source is selected, with a
   * Change button to re-expand. Reclaims vertical space on long wizards (HiPo specifically). */
  collapseOnSelect?: boolean;
}

/** Shared single-select picker for an existing campaign that the new optimisation
 * campaign will derive context from (Google Ads account, business id, locations).
 *
 * Used by the Location, HiPo, and ICP wizards. Sorts by `created_at` descending
 * so the user's most recent prospecting campaigns surface first, which is the
 * common case for "I just spun up a prospecting campaign and now want a derived
 * one." Includes a search box that filters by name + goal + URL, and a soft
 * visible cap so orgs with hundreds of campaigns don't render hundreds of
 * radio cards at once.
 *
 * Every campaign matching the wizard's goal filter is clickable — DRAFT and
 * CREATING campaigns are no longer disabled because the HiPo wizard's keyword
 * multi-select step lets the user pick keywords by hand even when no
 * performance data exists yet.
 */
export function SourceCampaignPicker({
  campaigns,
  selectedId,
  onSelect,
  visibleCap = 50,
  emptyState,
  getDisabledReason,
  isLoading = false,
  collapseOnSelect = false,
}: SourceCampaignPickerProps) {
  const [query, setQuery] = useState('');
  // Collapse state for collapseOnSelect mode. Defaults to expanded; collapses automatically
  // once the parent reports a selected id; user can re-expand via the Change button.
  const [isExpanded, setIsExpanded] = useState(selectedId === null);
  useEffect(() => {
    if (collapseOnSelect && selectedId !== null) setIsExpanded(false);
  }, [collapseOnSelect, selectedId]);

  const selectedCampaign = useMemo(
    () => campaigns.find((c) => c.id === selectedId),
    [campaigns, selectedId]
  );

  const sortedCampaigns = useMemo(() => {
    return [...campaigns].sort((a, b) => {
      // Most recent first; fall back to id desc if created_at is missing.
      if (a.created_at && b.created_at) {
        return b.created_at.localeCompare(a.created_at);
      }
      return b.id - a.id;
    });
  }, [campaigns]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedCampaigns;
    return sortedCampaigns.filter((c) => {
      return (
        c.name?.toLowerCase().includes(q) ||
        c.goal?.toLowerCase().includes(q) ||
        c.website_url?.toLowerCase().includes(q)
      );
    });
  }, [sortedCampaigns, query]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-[76px] rounded-lg border-2 border-gray-200 bg-gray-100 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (campaigns.length === 0) {
    return (
      <>
        {emptyState ?? (
          <div className="text-center py-8 text-gray-500">
            No eligible source campaigns yet. Create a Prospecting campaign first.
          </div>
        )}
      </>
    );
  }

  const visible = filtered.slice(0, visibleCap);
  const hidden = filtered.length - visible.length;
  const showCollapsed = collapseOnSelect && !isExpanded && !!selectedCampaign;

  return (
    <div className="space-y-3">
      {!showCollapsed && campaigns.length > 5 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search campaigns by name, type, or URL..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      )}

      {!showCollapsed && filtered.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No campaigns match &quot;{query}&quot;.
        </div>
      ) : (
        <>
          <div className={showCollapsed ? '' : 'max-h-[420px] overflow-y-auto space-y-3 pr-1'}>
            <AnimatePresence initial={false}>
            {showCollapsed && selectedCampaign && (
              <motion.div
                key="collapsed"
                initial={{ opacity: 0, y: 8 }}
                // Entry waits for the rows to finish their staggered exit so the summary
                // slides into the space they vacated; exit is immediate (no delay) so the
                // BACK / Change action doesn't leave a ghost card lingering.
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.2, ease: 'easeOut', delay: visible.length * 0.04 + 0.12 },
                }}
                exit={{
                  opacity: 0,
                  y: 8,
                  transition: { duration: 0.15, ease: 'easeOut' },
                }}
                className="flex items-center space-x-4 p-4 rounded-lg border-2 border-[#290BD9] bg-[#290BD9]/5"
              >
                <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-[#290BD9] flex items-center justify-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#290BD9]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate text-[#290BD9]">{selectedCampaign.name}</div>
                  <div className="text-sm text-gray-500 mt-1 truncate">
                    {selectedCampaign.goal} · {selectedCampaign.website_url}
                  </div>
                </div>
                {/* No point offering Change when there's nothing to change to (#5832). */}
                {campaigns.length > 1 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => setIsExpanded(true)}
                    className="h-8 px-2 flex-shrink-0 flex items-center gap-1 text-[#290BD9] hover:text-[#2309C5] hover:bg-gray-50"
                  >
                    <Pencil className="h-3 w-3" />
                    Change
                  </Button>
                )}
              </motion.div>
            )}
            {!showCollapsed && visible.map((c, idx) => {
              const isSelected = selectedId === c.id;
              const disabledReason = getDisabledReason?.(c) ?? null;
              const isDisabled = disabledReason !== null;
              return (
                <motion.button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    if (isDisabled) return;
                    onSelect(c.id);
                    if (collapseOnSelect) setIsExpanded(false);
                  }}
                  disabled={isDisabled}
                  aria-disabled={isDisabled}
                  // Staggered exit when the picker collapses: each row fades and shrinks
                  // up, briefly stagger-revealing the selected one before the summary
                  // takes its place. Selected row exits last so it feels like it's
                  // "absorbed" into the collapsed card.
                  exit={{
                    opacity: 0,
                    y: -8,
                    height: 0,
                    marginTop: 0,
                    paddingTop: 0,
                    paddingBottom: 0,
                    borderWidth: 0,
                  }}
                  transition={{
                    duration: 0.22,
                    ease: 'easeOut',
                    delay: isSelected ? idx * 0.04 + 0.08 : idx * 0.04,
                  }}
                  className={`w-full flex items-start space-x-4 p-4 rounded-lg border-2 transition-colors text-left overflow-hidden ${
                    isDisabled
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                      : isSelected
                      ? 'border-[#290BD9] bg-[#290BD9]/10'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div
                    className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                      isDisabled
                        ? 'border-gray-300 opacity-50'
                        : isSelected
                        ? 'border-[#290BD9]'
                        : 'border-gray-300'
                    }`}
                  >
                    {isSelected && !isDisabled && <div className="w-2.5 h-2.5 rounded-full bg-[#290BD9]" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`font-medium truncate ${
                        isDisabled
                          ? 'text-gray-500'
                          : isSelected
                          ? 'text-[#290BD9]'
                          : 'text-gray-900'
                      }`}
                    >
                      {c.name}
                    </div>
                    <div className="text-sm text-gray-500 mt-1 truncate">
                      {c.goal} · {c.website_url}
                    </div>
                    {disabledReason && (
                      <div className="text-sm text-gray-600 mt-2">{disabledReason}</div>
                    )}
                  </div>
                </motion.button>
              );
            })}
            </AnimatePresence>
          </div>
          {!showCollapsed && hidden > 0 && (
            <div className="text-sm text-gray-500 text-center pt-2 border-t border-gray-100">
              +{hidden} more — refine your search to see them
            </div>
          )}
        </>
      )}
    </div>
  );
}
